import { nztmToLatLng, validateLatLng, validateNZTM } from '@msaf/maps-common'
import { useState, useEffect, useRef } from 'react'
import { DEFAULT_ZOOM } from '../components/aoraki/generic-search-maps-react/src/config/constants'

export enum CoordinateTypes {
  NZTM = 'NZTM',
  LatLng = 'Latitude/Longitude',
  Lat = 'Latitude',
  Lng = 'Longitude',
  Easting = 'NZTM Easting',
  Northing = 'NZTM Northing',
}

interface UseInlineMapProps {
  xCoordinate?: string
  yCoordinate?: string
  projectionType?: string
}

export const LOCATION_FIELD_DEFAULT = {
  NO_LOCATION: DEFAULT_ZOOM,
  LOCATION_SET: 18,
  NZ_CENTRE: {
    easting: 1576000,
    northing: 5407000,
  },
}

export function validateCoordinates(
  coordinates: [string, string] | [number, number],
  projectionType?: string,
): boolean {
  const [x, y] = coordinates
  let isValid = false

  // Validate coordinates based on the selected projection (NZTM/LatLng)
  if (projectionType === CoordinateTypes.LatLng) {
    isValid = validateLatLng(x, y)
  } else {
    // Assumes NZTM as the default projection type
    isValid = validateNZTM(x, y)
  }

  return isValid
}

export default function useInlineMap({ xCoordinate, yCoordinate, projectionType }: UseInlineMapProps) {
  const [startPosition, setStartPosition] = useState<[number, number]>()
  // Default to undefined to allow new record validation
  const [isValidCoordinates, setIsValidCoordinates] = useState<boolean>()
  // Stores the latest projection type value
  // to be used inside the map's setLocation handler
  const projectionRef = useRef(projectionType)

  function updateProjectionRef(value: string) {
    projectionRef.current = value
  }

  useEffect(() => {
    if (xCoordinate && yCoordinate) {
      let isValid = validateCoordinates([xCoordinate, yCoordinate], projectionType)
      if (isValid) {
        // We can parse values to float safely as the validation has passed
        let startPos: [number, number] = [parseFloat(xCoordinate), parseFloat(yCoordinate)]
        if (projectionType !== CoordinateTypes.LatLng) {
          // Convert to Lat Lng
          try {
            startPos = nztmToLatLng(startPos)
          } catch {
            // Should ideally never reach here
            isValid = false
          }
        }
        setStartPosition(startPos)
      }
      setIsValidCoordinates(isValid)
    }

    // Update the projectype ref value
    if (projectionType) {
      updateProjectionRef(projectionType)
    }

    // Verify coordinates and set the start position on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [xCoordinate, yCoordinate, projectionType])

  return {
    startPosition,
    projectionRef,
    isValidCoordinates,
    setIsValidCoordinates,
  }
}
